import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useContent, useNumber, useT } from '@/lib/i18n';
import type { ResultEnvelopeDto } from '@/lib/api';

/**
 * precision.tsx — SE(θ), in the one read where it is not a trap (N4).
 *
 * ### Why this is not on the bar
 *
 * `plan-preproduction` N4 says *"the SE is the cheap, high-value companion —
 * render it as a confidence band on the bar, never as a second number"*, and the
 * obvious reading of that sentence is the wrong one. There are two estimators in
 * a technical result and they do not share a scale:
 *
 *   - `display.dimensions[].value` is a normalised 0–100 score from a weighted
 *     mean of item scores. `display.dimensions[].interval` is ITS interval, in
 *     display units, and that is what `BarsViz` draws.
 *   - `score.skills[].theta` is an IRT ability estimate on the **logit** scale,
 *     and `standardError` is `1/√I(θ)` — the precision of *that*.
 *
 * The bar is not derived from θ. Projecting SE(θ) onto it would invent a
 * relationship between two different estimators, and — this is the part that
 * makes it worth a file of its own — every number would still look plausible.
 * Nobody reviewing the screen would catch it.
 *
 * ### So it goes where a reader is already reading provenance
 *
 * The technical read, beside the config version and the instrument version, with
 * its scale named in the description. A psychometrician auditing reproducibility
 * is the reader who wants it and the one who can use it; a counsellor is neither.
 *
 * ### Presence, never a model
 *
 * The card renders if the envelope carries an entry with a `standardError` and
 * not otherwise. It never asks which model produced the block — a fourth plugin
 * that estimates on a latent scale lights this up with no edit here, and the
 * three that do not never see it. `standardError` is absent whenever `theta` is,
 * by contract, so there is no row claiming a precision for an estimate that was
 * never made.
 */
export function PrecisionCard({ skills }: { skills: ResultEnvelopeDto['score']['skills'] }) {
  const t = useT();
  const content = useContent();
  const number = useNumber();

  // `flatMap` rather than `filter`, so the rows below are typed as carrying a
  // standard error rather than asserted to.
  const rows = (skills ?? []).flatMap((skill) =>
    skill.standardError === undefined
      ? []
      : [{ label: skill.label, theta: skill.theta, standardError: skill.standardError }],
  );
  if (rows.length === 0) return null;

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-base">{t('result.precision')}</CardTitle>
        {/* The description does one job: name the scale. A reader who takes
            SE = 0.31 to be three tenths of a point on a 0–100 bar has been
            misled by the layout, not by the number. */}
        <CardDescription>{t('result.precision.hint')}</CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('result.dimension')}</TableHead>
              {/* θ and SE(θ) are notation, not English — they are the same
                  symbols in an Arabic psychometrics report, which is why the
                  header keys resolve to the same glyphs in both catalogues. */}
              <TableHead className="w-28">{t('result.precision.theta')}</TableHead>
              <TableHead className="w-28">{t('result.precision.se')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={`${content(row.label)}-${index}`}>
                {/* The envelope's own label. There is no dimension KEY on this
                    block to look up in the catalogue, and inventing a mapping
                    from the label back to a key would be a guess. */}
                <TableCell>{content(row.label)}</TableCell>
                <TableCell className="font-mono text-xs" dir="ltr">
                  {row.theta === undefined ? '' : number(row.theta)}
                </TableCell>
                <TableCell className="font-mono text-xs" dir="ltr">
                  {number(row.standardError)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
